import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface MapDataProject {
  id: number;
  name: string;
  sales_nav_url: string | null;
  job_titles: string | null;
  sales_nav_result_count: number | null;
  created_at: string;
  candidate_count?: number;
}

const MapData: React.FC = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState<MapDataProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState('');
  const [jobTitles, setJobTitles] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [editingProject, setEditingProject] = useState<number | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [editResultCount, setEditResultCount] = useState('');
  const [saving, setSaving] = useState(false);
  // Page-based pagination for the saved-projects list. PAGE_SIZE rows
  // shown at a time with Prev/Next buttons. Resets to 1 whenever the
  // underlying list changes (new entry added, refreshed, deleted).
  const PAGE_SIZE = 30;
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(projects.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const visibleProjects = projects.slice(pageStart, pageEnd);

  // Snap back to page 1 if the list shrinks below the current page.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const fetchEntries = async () => {
    try {
      const resp = await fetch('/api/map-data', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setProjects(data.entries || []);
      }
    } catch (err) {
      console.error('Failed to fetch map data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [token]);

  const buildSalesNavUrl = (titles: string): string => {
    const titleList = titles
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    if (titleList.length === 0) {
      return 'https://www.linkedin.com/sales/search/people';
    }

    const titleValues = titleList.map(title => {
      const encodedTitle = encodeURIComponent(title)
        .replace(/\(/g, '%28')
        .replace(/\)/g, '%29')
        .replace(/%/g, '%25');
      return `(text%3A${encodedTitle}%2CselectionType%3AINCLUDED)`;
    }).join('%2C');

    const titleFilter = `(type%3ACURRENT_TITLE%2Cvalues%3AList(${titleValues}))`;
    return `https://www.linkedin.com/sales/search/people#query=(filters%3AList(${titleFilter}))&viewAllFilters=true`;
  };

  const handleCreateAndOpen = async () => {
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }
    if (!jobTitles.trim()) {
      setError('Enter at least one job title');
      return;
    }

    setCreating(true);
    setError('');

    const salesNavUrl = buildSalesNavUrl(jobTitles);

    try {
      const resp = await fetch('/api/map-data', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: projectName.trim(),
          job_titles: jobTitles.trim(),
          sales_nav_url: salesNavUrl
        })
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to create entry');
      }

      // Open Sales Nav
      window.open(salesNavUrl, '_blank');

      // Reset form and refresh
      setProjectName('');
      setJobTitles('');
      await fetchEntries();
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleStartEdit = (project: MapDataProject) => {
    setEditingProject(project.id);
    setEditUrl(project.sales_nav_url || '');
    setEditResultCount(project.sales_nav_result_count?.toString() || '');
  };

  const handleDelete = async (project: MapDataProject) => {
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`)) return;
    try {
      const resp = await fetch(`/api/map-data/${project.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        await fetchEntries();
      } else {
        const data = await resp.json().catch(() => ({}));
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Failed to delete');
    }
  };

  const handleSaveEdit = async (projectId: number) => {
    setSaving(true);
    try {
      const resp = await fetch(`/api/map-data/${projectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sales_nav_url: editUrl || null,
          sales_nav_result_count: editResultCount ? parseInt(editResultCount, 10) : null
        })
      });
      if (resp.ok) {
        setEditingProject(null);
        await fetchEntries();
      }
    } catch (err) {
      console.error('Failed to update project:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Map Data</h2>
        <p className="text-gray-500 mt-1">Create projects and open Sales Navigator searches with job titles</p>
      </div>

      {/* New Mapping Form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-[#0077B5]" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
          New Market Mapping
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g. Senior .NET Developers - Coventry"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Titles
            </label>
            <textarea
              value={jobTitles}
              onChange={(e) => setJobTitles(e.target.value)}
              placeholder="e.g. Software Developer, Software Engineer, .NET Developer, Full Stack Developer, Backend Developer, C# Developer"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={3}
            />
            <p className="text-xs text-gray-400 mt-1">
              Separate titles with commas. These will be used as current job title filters in Sales Navigator.
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            onClick={handleCreateAndOpen}
            disabled={creating}
            className="w-full px-4 py-2.5 bg-[#0077B5] text-white rounded-md hover:bg-[#006097] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {creating ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Creating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Create Project & Open Sales Navigator
              </>
            )}
          </button>
        </div>
      </div>

      {/* Recent Projects */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Recent Projects</h3>
        </div>

        {loading ? (
          <div className="px-6 py-8 text-center text-gray-400">Loading...</div>
        ) : projects.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-400">No projects yet</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleProjects.map(project => (
              <div key={project.id} className="px-6 py-3 hover:bg-gray-50">
                {editingProject === project.id ? (
                  /* Edit mode */
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-900">{project.name}</p>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Sales Nav URL</label>
                      <input
                        type="text"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        placeholder="Paste updated Sales Navigator URL here"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Result Count</label>
                      <input
                        type="number"
                        value={editResultCount}
                        onChange={(e) => setEditResultCount(e.target.value)}
                        placeholder="Number of results in Sales Nav"
                        className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(project.id)}
                        disabled={saving}
                        className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingProject(null)}
                        className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{project.name}</p>
                      <p className="text-xs text-gray-400">
                        {formatDate(project.created_at)}
                        {project.job_titles && (
                          <span className="ml-2">
                            Titles: {project.job_titles.length > 60 ? project.job_titles.substring(0, 57) + '...' : project.job_titles}
                          </span>
                        )}
                        {project.sales_nav_result_count != null && (
                          <span className="ml-2 text-blue-500 font-medium">
                            {project.sales_nav_result_count.toLocaleString()} results
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {project.sales_nav_url && (
                        <button
                          onClick={() => window.open(project.sales_nav_url!, '_blank')}
                          className="px-3 py-1 text-xs font-medium text-[#0077B5] border border-[#0077B5] rounded-md hover:bg-blue-50"
                          title="Open Sales Navigator search"
                        >
                          Sales Nav
                        </button>
                      )}
                      <button
                        onClick={() => handleStartEdit(project)}
                        className="px-3 py-1 text-xs font-medium text-gray-500 border border-gray-300 rounded-md hover:bg-gray-50"
                        title="Edit Sales Nav URL and result count"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(project)}
                        className="px-3 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                        title="Delete this saved search"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pager — only renders when there's more than one page. */}
        {!loading && projects.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 text-xs text-gray-600">
            <span>
              Showing {pageStart + 1}–{Math.min(pageEnd, projects.length)} of {projects.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapData;
